import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { LifeBuoy, Star, Clock, X, CreditCard, Loader2, CheckCircle2, CalendarClock, Send } from "lucide-react";

const euro = (n) => (n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const CONSULT_STATUS = {
  pending: { label: "En attente de paiement", cls: "bg-amber-50 text-amber-700" },
  paye: { label: "Payée", cls: "bg-emerald-50 text-emerald-700" },
};
const BOOK_STATUS = {
  pending: { label: "En attente", cls: "bg-amber-50 text-amber-700" },
  accepted: { label: "Acceptée", cls: "bg-emerald-50 text-emerald-700" },
  refused: { label: "Refusée", cls: "bg-rose-50 text-rose-700" },
};

export default function Experts() {
  const [experts, setExperts] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [active, setActive] = useState(null);
  const [hours, setHours] = useState(1);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  // availability booking
  const [slotExpert, setSlotExpert] = useState(null);
  const [slots, setSlots] = useState([]);
  const [chosenSlot, setChosenSlot] = useState(null);
  const [slotMessage, setSlotMessage] = useState("");

  const load = () => {
    api.get("/experts").then((r) => setExperts(r.data)).catch(() => {});
    api.get("/consultations").then((r) => setConsultations(r.data)).catch(() => {});
    api.get("/my-bookings").then((r) => setBookings(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const book = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/consultations/checkout", {
        expert_id: active.id, hours, message, origin_url: window.location.origin,
      });
      if (data.demo) { toast.info(data.message); setLoading(false); return; }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Erreur lors de la réservation");
      setLoading(false);
    }
  };

  const openSlots = async (e) => {
    setSlotExpert(e); setChosenSlot(null); setSlotMessage("");
    try {
      const { data } = await api.get(`/experts/${e.id}/slots`);
      setSlots(data);
    } catch (_) { setSlots([]); }
  };

  const requestSlot = async () => {
    if (!chosenSlot) return toast.error("Choisissez un créneau");
    try {
      await api.post(`/experts/${slotExpert.id}/book`, { slot_id: chosenSlot, message: slotMessage });
      toast.success("Demande envoyée ! L'expert va la traiter.");
      setSlotExpert(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-navy flex items-center justify-center"><LifeBuoy className="w-5 h-5 text-sky-400" /></div>
        <div>
          <h1 className="text-3xl font-display font-bold text-navy">Renfort Expert</h1>
          <p className="text-slate-500">Réservez un créneau ou une consultation avec un expert pour vos devis et chantiers</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {experts.map((e) => (
          <div key={e.id} className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col" data-testid={`expert-${e.id}`}>
            <div className="flex items-center gap-3">
              <img src={e.photo} alt={e.name} className="w-16 h-16 rounded-2xl object-cover" onError={(ev) => (ev.target.style.visibility = "hidden")} />
              <div>
                <h3 className="font-display font-semibold text-navy text-lg leading-tight">{e.name}</h3>
                <p className="text-sm text-sky-600">{e.specialty}</p>
                <div className="flex items-center gap-1 text-xs text-amber-500 mt-0.5">
                  <Star className="w-3.5 h-3.5 fill-amber-400" /> {e.rating} <span className="text-slate-400">({e.reviews} avis)</span>
                </div>
              </div>
            </div>
            <p className="text-sm text-slate-500 mt-3 flex-1">{e.bio}</p>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
              <span className="text-lg font-bold text-navy tabular">{euro(e.hourly_rate)}<span className="text-sm font-normal text-slate-400">/h</span></span>
              <div className="flex gap-2">
                <button onClick={() => openSlots(e)} data-testid={`slots-${e.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-sky-400 text-navy text-sm font-semibold hover:bg-sky-500 transition-colors">
                  <CalendarClock className="w-4 h-4" /> Dispos
                </button>
                <button onClick={() => { setActive(e); setHours(1); setMessage(""); }} data-testid={`book-${e.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy-deep transition-colors">
                  <Clock className="w-4 h-4" /> Payer
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {bookings.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="my-bookings">
          <h3 className="font-display font-semibold text-navy text-lg">Mes demandes de réservation</h3>
          <div className="mt-3 divide-y divide-slate-100">
            {bookings.map((bk) => (
              <div key={bk.id} className="flex items-center justify-between py-3" data-testid={`my-booking-${bk.id}`}>
                <div>
                  <div className="text-sm font-medium text-navy">{bk.expert_name}</div>
                  <div className="text-xs text-slate-500 font-mono">{bk.date} · {bk.start_time}–{bk.end_time}</div>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${BOOK_STATUS[bk.status]?.cls}`}>{BOOK_STATUS[bk.status]?.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {consultations.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-display font-semibold text-navy text-lg">Mes consultations payantes</h3>
          <div className="mt-3 divide-y divide-slate-100">
            {consultations.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-3" data-testid={`consultation-${c.id}`}>
                <div className="flex items-center gap-3">
                  {c.status === "paye" ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Clock className="w-5 h-5 text-amber-500" />}
                  <div>
                    <div className="text-sm font-medium text-navy">{c.expert_name} · {c.hours}h</div>
                    <div className="text-xs text-slate-500">{c.expert_specialty}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-navy tabular">{euro(c.amount)}</span>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${CONSULT_STATUS[c.status]?.cls || "bg-slate-100 text-slate-600"}`}>{CONSULT_STATUS[c.status]?.label || c.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Paid consultation modal */}
      {active && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="booking-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-display font-bold text-navy">Consultation payante — {active.name}</h2>
              <button onClick={() => setActive(null)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <label className="text-sm font-medium text-slate-700">Durée de la consultation</label>
            <div className="flex gap-2 mt-2">
              {[1, 2, 3].map((h) => (
                <button key={h} onClick={() => setHours(h)} data-testid={`hours-${h}`}
                  className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-colors ${hours === h ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {h}h
                </button>
              ))}
            </div>
            <label className="text-sm font-medium text-slate-700 mt-4 block">Votre besoin (optionnel)</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} data-testid="booking-message" placeholder="Décrivez votre devis / chantier…"
              className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
            <div className="flex items-center justify-between mt-4 p-3 rounded-xl bg-slate-50">
              <span className="text-sm text-slate-600">Total à payer</span>
              <span className="text-xl font-bold text-navy tabular" data-testid="booking-total">{euro(active.hourly_rate * hours)}</span>
            </div>
            <button onClick={book} disabled={loading} data-testid="confirm-booking-btn"
              className="mt-4 w-full py-3 rounded-xl bg-navy text-white font-semibold hover:bg-navy-deep transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Payer & réserver
            </button>
            <p className="text-[11px] text-slate-400 text-center mt-2">Paiement sécurisé · carte test 4242 4242 4242 4242</p>
          </div>
        </div>
      )}

      {/* Availability slots modal */}
      {slotExpert && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setSlotExpert(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="slots-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-display font-bold text-navy">Disponibilités — {slotExpert.name}</h2>
              <button onClick={() => setSlotExpert(null)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            {slots.length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center">Aucun créneau disponible pour cet expert.</p>
            ) : (
              <div className="space-y-2">
                {slots.map((s) => (
                  <button key={s.id} onClick={() => setChosenSlot(s.id)} data-testid={`choose-slot-${s.id}`}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-left transition-colors ${chosenSlot === s.id ? "border-sky-400 bg-sky-50" : "border-slate-200 hover:border-sky-300"}`}>
                    <div>
                      <div className="text-sm font-semibold text-navy font-mono">{s.date}</div>
                      <div className="text-xs text-slate-500">{s.start_time}–{s.end_time}{s.note ? ` · ${s.note}` : ""}</div>
                    </div>
                    {chosenSlot === s.id && <CheckCircle2 className="w-5 h-5 text-sky-500" />}
                  </button>
                ))}
              </div>
            )}
            {slots.length > 0 && (
              <>
                <textarea value={slotMessage} onChange={(e) => setSlotMessage(e.target.value)} rows={2} data-testid="slot-request-message" placeholder="Message pour l'expert (optionnel)…"
                  className="mt-3 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
                <button onClick={requestSlot} data-testid="request-slot-btn"
                  className="mt-3 w-full py-3 rounded-xl bg-navy text-white font-semibold hover:bg-navy-deep transition-colors inline-flex items-center justify-center gap-2">
                  <Send className="w-4 h-4" /> Envoyer la demande
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
